import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { getImageUrl } from "@/lib/images";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageCircle } from "lucide-react";
import useSocket from "../hooks/useSocket";

export default function Chats() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [activeId, setActiveId] = useState(params.get("id"));
  const [messages, setMessages] = useState([]);
  const [chatMeta, setChatMeta] = useState(null);
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const forceScrollRef = useRef(false);
  const { socket, joinChat, leaveChat } = useSocket();

  const loadChats = async () => {
    const r = await api.get("/chats");
    setChats(r.data);
    if (!activeId && r.data.length > 0) {
      setActiveId(r.data[0].id);
    }
  };

  const loadMessages = async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/chats/${id}/messages`);
      setMessages(r.data.messages);
      setChatMeta(r.data.chat);
    } catch (e) {
      // chat may not exist
    }
  };

  useEffect(() => { loadChats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    if (!activeId) return;
    forceScrollRef.current = true;
    stickToBottomRef.current = true;
    loadMessages(activeId);
    setParams({ id: activeId });
    joinChat(activeId);
    return () => {
      leaveChat(activeId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    if (!socket) return;
    const handler = (msg) => {
      if (msg.chat_id === activeId) {
        setMessages((prev) => [...prev, msg]);
      } else {
        loadChats();
      }
    };
    socket.on("chat:new_message", handler);
    return () => socket.off("chat:new_message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, activeId]);

  useEffect(() => {
    const scrollToBottom = (behavior) => {
      const el = listRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
    };

    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      requestAnimationFrame(() => scrollToBottom("auto"));
      return;
    }
    if (!stickToBottomRef.current) return;
    requestAnimationFrame(() => scrollToBottom("smooth"));
  }, [messages]);

  const onScrollMessages = () => {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 120;
  };

  const send = (e) => {
    e?.preventDefault();
    if (!text.trim() || !activeId || !socket) return;
    socket.emit("chat:message", { chatId: activeId, text });
    setText("");
  };

  const otherName = (c) => (c.player_id === user?.id ? c.owner_name : c.player_name);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10 py-10">
        <div className="label-eyebrow text-volt mb-2">{t("chat.eyebrow")}</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-8">{t("chat.title")}</h1>

        <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-1 border border-border bg-card min-h-[60vh]">
          {/* Thread list */}
          <div className="border-r border-border max-h-[70vh] overflow-y-auto">
            {chats.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">
                <MessageCircle className="h-8 w-8 mx-auto mb-3 text-volt/60" />
                {t("chat.no_threads")}
              </div>
            ) : (
              chats.map((c) => (
                <button
                  key={c.id}
                  data-testid={`chat-thread-${c.id}`}
                  onClick={() => setActiveId(c.id)}
                  className={`w-full flex items-center gap-3 p-4 border-b border-border/30 hover:bg-muted text-left transition-colors ${
                    activeId === c.id ? "bg-muted border-l-2 border-l-volt" : ""
                  }`}
                >
                  <div className="w-12 h-12 overflow-hidden border border-border flex-shrink-0">
                    {c.court_image ? (
                      <img src={getImageUrl(c.court_image, 'thumbnail')} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-muted" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{otherName(c)}</div>
                    <div className="text-[10px] text-muted-foreground tracking-widest uppercase truncate">
                      {c.court_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {c.last_message || "—"}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Conversation */}
          <div className="flex flex-col max-h-[70vh]">
            {!chatMeta ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                {t("chat.select_thread")}
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border flex items-center gap-3">
                  <div className="w-10 h-10 overflow-hidden border border-border">
                    {chatMeta.court_image ? (
                      <img src={getImageUrl(chatMeta.court_image, 'thumbnail')} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-muted" />
                    )}
                  </div>
                  <div>
                    <div className="font-heading text-xl tracking-tight">{otherName(chatMeta)}</div>
                    <div className="text-[10px] text-muted-foreground tracking-widest uppercase">
                      {chatMeta.court_name}
                    </div>
                  </div>
                </div>
                <div
                  ref={listRef}
                  onScroll={onScrollMessages}
                  className="flex-1 overflow-y-auto p-4 space-y-3"
                  data-testid="message-list"
                >
                  {messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[70%] p-3 text-sm ${
                            mine
                              ? "bg-volt text-primary-foreground font-medium"
                              : "bg-muted border border-border/30 text-foreground"
                          }`}
                        >
                          {m.text}
                          <div className={`mt-1 text-[10px] tracking-widest uppercase ${mine ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="border-t border-border p-3 flex gap-2">
                  <input
                    data-testid="message-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t("chat.type_message")}
                    className="flex-1 bg-background border border-input px-4 py-3 text-sm focus:border-volt focus:outline-none"
                  />
                  <button
                    type="submit"
                    data-testid="send-message-btn"
                    className="btn-volt px-5 py-3 flex items-center gap-2 text-xs"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {t("chat.send")}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
